<template>
  <div class="admin-page">
    <admin-page-header
      eyebrow="Paraşüt"
      title="Garanti Hesabı"
      description="Paraşüt'e aktarılmış banka hareketlerini ve hesap bakiyesini görüntüleyin."
    >
      <template #actions>
        <v-btn
          v-if="isAdmin && status.configured"
          small
          text
          :disabled="busy"
          @click="startAuthorization"
        >
          Bağlantıyı yetkilendir
        </v-btn>
        <v-btn
          v-if="isAdmin && status.connected"
          small
          outlined
          :disabled="busy"
          @click="openAccounts"
        >
          Hesap seç
        </v-btn>
        <v-btn
          small
          outlined
          color="primary"
          :loading="loading"
          :disabled="busy"
          @click="refresh"
        >
          Yenile
        </v-btn>
      </template>
    </admin-page-header>

    <v-alert
      v-if="error"
      type="error"
      outlined
      dense
    >
      {{ error }}
    </v-alert>
    <v-alert
      v-if="!loading && !error && !status.configured"
      type="info"
      outlined
    >
      Paraşüt bağlantısı henüz yapılandırılmadı. Sunucu ayarları ve bağlantı migration'ı tamamlanmalı. Henüz finansal veri çekilmedi.
    </v-alert>
    <v-alert
      v-else-if="!loading && !error && !status.connected"
      type="info"
      outlined
    >
      Paraşüt hesabı yetkilendirilmedi. Bir yönetici bağlantıyı açıp Paraşüt üzerinde izin vermeli. Şifrenizi bu panele girmeyin.
    </v-alert>
    <v-alert
      v-else-if="!loading && !error && !status.accountId"
      type="info"
      outlined
    >
      Bağlantı hazır. Görüntülenecek Garanti banka hesabını bir yönetici seçmeli.
    </v-alert>

    <template v-if="overview">
      <admin-metric-strip
        :items="metrics"
        :loading="loading"
      />
      <p class="text-caption text--secondary mt-3">
        Salt okunur · Şirket #{{ status.companyId }} · Hesap #{{ overview.account.id }} · {{ overview.account.maskedIban || 'IBAN belirtilmemiş' }}
        <br>Son okuma, panelin Paraşüt API'sinden veri aldığı zamandır; bankanın son senkronizasyon zamanı değildir.
      </p>
      <v-alert
        v-if="overview.account.archived"
        type="warning"
        outlined
        dense
      >
        Bu hesap Paraşüt'te arşivlenmiş.
      </v-alert>
    </template>

    <v-card
      v-if="status.accountId"
      class="admin-table-card mt-5"
      outlined
    >
      <v-form
        class="admin-page-toolbar garanti-toolbar"
        @submit.prevent="applyDate"
      >
        <h2 class="text-h6">
          Hesap hareketleri
        </h2>
        <v-text-field
          v-model="date"
          label="İşlem günü (isteğe bağlı)"
          type="date"
          outlined
          dense
          hide-details
          clearable
          :disabled="loading"
        />
        <v-btn
          type="submit"
          color="primary"
          outlined
          small
          :disabled="loading"
        >
          Uygula
        </v-btn>
        <span class="text-caption text--secondary">Yeni işlem ID'sinden eskiye</span>
      </v-form>
      <v-data-table
        :headers="headers"
        :items="movements"
        :loading="loading"
        :server-items-length="totalCount"
        :page="page"
        :items-per-page="size"
        :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }"
        class="admin-data-table"
        disable-sort
        @update:page="changePage"
        @update:items-per-page="changeSize"
      >
        <template #item.date="{ item }">
          {{ formatDay(item.date) }}
        </template>
        <template #item.description="{ item }">
          <div class="movement-description">
            {{ item.description || 'Açıklama yok' }}
          </div>
        </template>
        <template #item.transactionType="{ item }">
          <v-chip
            small
            outlined
            :title="item.transactionType"
          >
            {{ transactionTypeLabel(item.transactionType, item.side) }}
          </v-chip>
        </template>
        <template #item.side="{ item }">
          <v-chip
            small
            outlined
          >
            {{ sideLabel(item.side) }}
          </v-chip>
        </template>
        <template #item.amount="{ item }">
          <span class="money">{{ formatMoney(item.amount, item.currency) }}</span>
        </template>
        <template #item.counterAccount="{ item }">
          {{ item.counterAccount || 'Belirtilmemiş' }}
        </template>
        <template #no-data>
          {{ error ? 'Veri yüklenemedi. Yenile ile tekrar deneyin.' : 'Bu sorgu için hesap hareketi bulunamadı.' }}
        </template>
      </v-data-table>
      <p class="text-caption text--secondary px-4 py-3 mb-0">
        Borç / alacak, seçili hesabın Paraşüt işlemindeki tarafıdır. Bu kayıtlar Adverport ödeme taleplerini veya muhasebe durumunu otomatik değiştirmez.
        50/100 satır seçiminde Paraşüt'ün 25'lik API sayfaları birleştirilir; yükleme biraz daha uzun sürebilir. Toplam geçmiş sınırı yoktur.
      </p>
    </v-card>

    <v-dialog
      v-model="authDialog"
      max-width="560"
      :persistent="busy"
    >
      <v-card>
        <v-card-title>Paraşüt bağlantısı</v-card-title>
        <v-card-text>
          <p>Paraşüt'te giriş yapıp erişim izni verin. Sonrasında gösterilen tek kullanımlık yetkilendirme kodunu aşağıya girin. Şifre veya API secret girmeyin.</p>
          <v-btn
            v-if="authorization.url"
            :href="authorization.url"
            target="_blank"
            rel="noopener noreferrer"
            outlined
            color="primary"
            class="mb-4"
          >
            Paraşüt'te yetkilendir
          </v-btn>
          <v-text-field
            v-model="code"
            label="Tek kullanımlık yetkilendirme kodu"
            type="password"
            autocomplete="off"
            outlined
            :disabled="busy"
          />
          <v-alert
            v-if="connectionError"
            type="error"
            dense
            outlined
          >
            {{ connectionError }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer /><v-btn
            text
            :disabled="busy"
            @click="closeAuthorization"
          >
            Vazgeç
          </v-btn><v-btn
            color="primary"
            :loading="busy"
            :disabled="!code.trim()"
            @click="completeAuthorization"
          >
            Bağla
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="accountsDialog"
      max-width="720"
      :persistent="busy"
    >
      <v-card>
        <v-card-title>Garanti hesabını seç</v-card-title>
        <v-card-text>
          <p>Şirket #{{ status.companyId }} banka hesapları. Yalnızca arşivlenmemiş Garanti hesabı bağlanabilir.</p>
          <v-alert
            v-if="connectionError"
            type="error"
            dense
            outlined
          >
            {{ connectionError }}
          </v-alert>
          <v-progress-linear
            v-if="accountsLoading"
            indeterminate
          />
          <v-list>
            <v-list-item
              v-for="account in accounts.items"
              :key="account.id"
              :disabled="busy || !account.garanti || account.archived"
              @click="selectAccount(account.id)"
            >
              <v-list-item-content><v-list-item-title>{{ account.name }}</v-list-item-title><v-list-item-subtitle>#{{ account.id }} · {{ account.bankName }} · {{ account.currency }} · {{ account.maskedIban }}{{ account.archived ? ' · Arşivlenmiş' : '' }}</v-list-item-subtitle></v-list-item-content>
              <v-list-item-action>
                <v-btn
                  text
                  small
                  :disabled="busy || !account.garanti || account.archived"
                  @click.stop="selectAccount(account.id)"
                >
                  Seç
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>
          <p v-if="!accountsLoading && !accounts.items.length">
            Banka hesabı bulunamadı.
          </p>
          <v-pagination
            v-if="accounts.totalPages > 1"
            :value="accounts.page"
            :length="accounts.totalPages"
            :disabled="accountsLoading || busy"
            @input="loadAccounts"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer /><v-btn
            text
            :disabled="busy"
            @click="accountsDialog = false"
          >
            Kapat
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import { getAccessToken, hasAdminAccess } from '@/utils/panelSession'
import { resolveApiErrorMessage } from '@/utils/apiErrors'
import AdminPageHeader from '@/components/admin/AdminPageHeader.vue'
import AdminMetricStrip from '@/components/admin/AdminMetricStrip.vue'
import {
  formatParasutReadAt, formatParasutMoney, formatParasutDay, transactionTypeLabel,
} from './parasutPresentation'

export default {
  components: { AdminPageHeader, AdminMetricStrip },
  data: () => ({
    status: {},
    overview: null,
    loading: false,
    error: '',
    requestId: 0,
    page: 1,
    size: 25,
    date: '',
    appliedDate: '',
    busy: false,
    authDialog: false,
    authorization: {},
    code: '',
    connectionError: '',
    accountsDialog: false,
    accountsLoading: false,
    accountsRequest: 0,
    accounts: { items: [], page: 1, totalPages: 0 },
    headers: [
      { text: 'İşlem ID', value: 'id', width: 110 }, { text: 'Tarih', value: 'date', width: 110 },
      { text: 'Açıklama', value: 'description' }, { text: 'İşlem türü', value: 'transactionType', width: 180 },
      { text: 'Hesap tarafı', value: 'side', width: 125 },
      {
        text: 'Tutar', value: 'amount', align: 'end', width: 150,
      }, { text: 'Karşı hesap', value: 'counterAccount' },
    ],
  }),
  computed: {
    isAdmin: () => hasAdminAccess(),
    requestOptions: () => ({ headers: { Authorization: `Bearer ${getAccessToken()}` } }),
    movements() { return this.overview?.movements?.items || [] },
    totalCount() { return this.overview?.movements?.totalCount || 0 },
    metrics() {
      if (!this.overview) return []
      const { account, readAt, movements } = this.overview

      return [
        { label: 'Paraşüt bakiyesi', value: this.formatMoney(account.balance, account.currency), helper: 'Bankanın anlık bakiyesi olmayabilir' },
        { label: 'Bağlı hesap', value: account.name, helper: account.bankName },
        { label: this.appliedDate ? 'Seçili günün hareketleri' : 'Toplam hesap hareketi', value: movements.totalCount, helper: 'Paraşüt kayıtları' },
        { label: 'Son başarılı okuma', value: formatParasutReadAt(readAt), helper: 'Türkiye saati' },
      ]
    },
  },
  mounted() { this.refresh() },
  beforeDestroy() { this.requestId += 1; this.accountsRequest += 1; this.code = ''; this.authorization = {} },
  methods: {
    endpoint(path) { return `${process.env.VUE_APP_BASE_API_URL}admin/parasut/${path}` },
    async refresh() {
      this.requestId += 1
      const id = this.requestId
      this.loading = true
      this.error = ''
      try {
        const result = await axios.get(this.endpoint('status'), this.requestOptions)
        if (id !== this.requestId) return
        this.status = result.data.data
        if (this.status.accountId) await this.fetchOverview(id)
        else this.overview = null
      } catch (error) { if (id === this.requestId) { this.overview = null; this.error = resolveApiErrorMessage(error, 'Paraşüt verisi yüklenemedi.') } } finally { if (id === this.requestId) this.loading = false }
    },
    async fetchOverview(id) {
      const result = await axios.get(this.endpoint('garanti'), { ...this.requestOptions, params: { page: this.page, size: this.size, date: this.appliedDate || undefined } })
      if (id !== this.requestId) return
      const { data } = result.data
      if (data.movements.totalPages > 0 && this.page > data.movements.totalPages) {
        this.page = data.movements.totalPages
        await this.fetchOverview(id)

        return
      }
      this.overview = data
    },
    changePage(page) { if (!this.loading && page !== this.page) { this.page = page; this.refresh() } },
    changeSize(size) { if (!this.loading && [10, 25, 50, 100].includes(size) && size !== this.size) { this.size = size; this.page = 1; this.refresh() } },
    applyDate() { this.appliedDate = this.date || ''; this.page = 1; this.refresh() },
    async startAuthorization() {
      this.busy = true
      this.error = ''
      this.code = ''
      this.connectionError = ''
      try {
        const result = await axios.post(this.endpoint('authorization/start'), {}, this.requestOptions)
        this.authorization = result.data.data
        this.authDialog = true
      } catch (error) { this.error = resolveApiErrorMessage(error, 'Yetkilendirme başlatılamadı.') } finally { this.busy = false }
    },
    closeAuthorization() { this.authDialog = false; this.authorization = {}; this.code = '' },
    async completeAuthorization() {
      if (this.busy || !this.code.trim()) return
      this.busy = true
      this.connectionError = ''
      try {
        await axios.post(this.endpoint('authorization/complete'), { code: this.code.trim(), nonce: this.authorization.nonce }, this.requestOptions)
        this.closeAuthorization()
        await this.refresh()
      } catch (error) {
        this.connectionError = `${resolveApiErrorMessage(error, 'Bağlantı kurulamadı.')} Pencereyi kapatıp yetkilendirmeyi yeniden başlatın.`
      } finally { this.busy = false; this.code = '' }
    },
    async openAccounts() { this.accountsDialog = true; await this.loadAccounts(1) },
    async loadAccounts(page) {
      this.accountsRequest += 1
      const id = this.accountsRequest
      this.accountsLoading = true
      this.connectionError = ''
      try {
        const result = await axios.get(this.endpoint('accounts'), { ...this.requestOptions, params: { page } })
        if (id === this.accountsRequest) this.accounts = result.data.data
      } catch (error) { if (id === this.accountsRequest) { this.accounts = { items: [], page: 1, totalPages: 0 }; this.connectionError = resolveApiErrorMessage(error, 'Hesaplar yüklenemedi.') } } finally { if (id === this.accountsRequest) this.accountsLoading = false }
    },
    async selectAccount(accountId) {
      if (this.busy) return
      this.busy = true
      this.connectionError = ''
      try {
        await axios.put(this.endpoint('account'), { accountId }, this.requestOptions)
        this.accountsDialog = false
        this.page = 1
        await this.refresh()
      } catch (error) { this.connectionError = resolveApiErrorMessage(error, 'Hesap bağlanamadı.') } finally { this.busy = false }
    },
    sideLabel(side) { return { DEBIT: 'Borç', CREDIT: 'Alacak' }[side] || 'Belirsiz' },
    formatDay: formatParasutDay,
    formatMoney: formatParasutMoney,
    transactionTypeLabel,
  },
}
</script>

<style scoped>
.garanti-toolbar { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; padding: 16px; }
.garanti-toolbar .v-input { flex: 0 1 250px; }
.movement-description { max-width: 440px; overflow-wrap: anywhere; white-space: normal; }
.money { font-variant-numeric: tabular-nums; white-space: nowrap; }
@media (max-width: 600px) { .garanti-toolbar .v-input { flex-basis: 100%; } }
</style>
